import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationSection = ({ formData, onChange, errors }) => {
  const [locations, setLocations] = useState(formData?.locations || [{ city: '', state: '', isRemote: false }]);

  const indianStates = [
    { value: 'andhra-pradesh', label: 'Andhra Pradesh' },
    { value: 'karnataka', label: 'Karnataka' },
    { value: 'maharashtra', label: 'Maharashtra' },
    { value: 'tamil-nadu', label: 'Tamil Nadu' },
    { value: 'telangana', label: 'Telangana' },
    { value: 'delhi', label: 'Delhi' },
    { value: 'gujarat', label: 'Gujarat' },
    { value: 'haryana', label: 'Haryana' },
    { value: 'kerala', label: 'Kerala' },
    { value: 'punjab', label: 'Punjab' },
    { value: 'rajasthan', label: 'Rajasthan' },
    { value: 'uttar-pradesh', label: 'Uttar Pradesh' },
    { value: 'west-bengal', label: 'West Bengal' }
  ];

  const workModes = [
    { value: 'office', label: 'On-site Only' },
    { value: 'remote', label: 'Remote Only' },
    { value: 'hybrid', label: 'Hybrid (Office + Remote)' }
  ];

  const addLocation = () => {
    setLocations([...locations, { city: '', state: '', isRemote: false }]);
  };

  const removeLocation = (index) => {
    const newLocations = locations?.filter((_, i) => i !== index);
    setLocations(newLocations);
    onChange('locations', newLocations);
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...locations];
    newLocations[index][field] = value;
    setLocations(newLocations);
    onChange('locations', newLocations);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="MapPin" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Location & Work Mode</h2>
          <p className="text-sm text-muted-foreground">Where will the employee work from</p>
        </div>
      </div>
      <div className="space-y-6">
        <Select
          label="Work Mode"
          options={workModes}
          value={formData?.workMode}
          onChange={(value) => onChange('workMode', value)}
          error={errors?.workMode}
          required
          placeholder="Select work mode"
          description="Choose how employees will work"
        />

        {formData?.workMode !== 'remote' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Office Locations <span className="text-error">*</span>
              </label>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={addLocation}
              >
                Add Location
              </Button>
            </div>

            <div className="space-y-4">
              {locations?.map((location, index) => (
                <div key={index} className="bg-muted/50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Location {index + 1}</span>
                    {locations?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLocation(index)}
                        className="text-error hover:text-error/80 transition-smooth"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="City"
                      type="text"
                      placeholder="e.g., Bangalore"
                      value={location?.city}
                      onChange={(e) => updateLocation(index, 'city', e?.target?.value)}
                      required
                    />

                    <Select
                      label="State"
                      options={indianStates}
                      value={location?.state}
                      onChange={(value) => updateLocation(index, 'state', value)}
                      required
                      placeholder="Select state"
                      searchable
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {formData?.workMode === 'remote' && (
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="Globe" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Remote Work Benefits</p>
                <p className="text-sm text-muted-foreground">
                  Remote positions typically receive 40% more applications and allow you to hire talent from across India
                </p>
              </div>
            </div>
          </div>
        )}

        <Checkbox
          label="Allow candidates from other locations to apply"
          description="Candidates willing to relocate can also apply for this position"
          checked={formData?.allowRelocation}
          onChange={(e) => onChange('allowRelocation', e?.target?.checked)}
        />
      </div>
    </div>
  );
};

export default LocationSection;